/** @format */
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core';
import { UniversitySettings, UserSettings } from 'common-typescript/types';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from 'sis-common';
import { MainNavigationItem, UniversityService, UserSettingsEntityService } from 'sis-components';
import { studentRoutes } from '../../../auth/student-routes';

const NEW_PLAN_LINK_ID: string = 'top-nav-student-studies-structure-wip';

const ALL_ITEMS: MainNavigationItem[] = [
    {
        translationKey: 'APPLICATION_STRUCTURE_OF_STUDIES',
        toState: 'student.logged-in.planSelect',
        loginRequired: true,
        linkId: 'top-nav-student-studies-structure',
    },
    {
        translationKey: 'APPLICATION_STRUCTURE_OF_STUDIES',
        toState: 'student.logged-in.new-plan',
        loginRequired: true,
        linkId: NEW_PLAN_LINK_ID,
    },
    {
        translationKey: 'APPLICATION_TIMING',
        toState: 'student.logged-in.plan.timing',
        loginRequired: true,
        linkId: 'top-nav-student-timing',
    },
    {
        translationKey: 'APPLICATION_ENROLMENTS',
        toState: 'student.logged-in.enrolments',
        loginRequired: true,
        linkId: 'top-nav-student-enrolments',
    },
    {
        translationKey: 'CALENDAR',
        toState: 'student.logged-in.calendar',
        loginRequired: true,
        linkId: 'top-nav-student-calendar',
    },
    {
        translationKey: 'APPLICATION_STUDENT_PROFILE',
        toState: 'student.logged-in.profile',
        loginRequired: true,
        linkId: 'top-nav-student-profile',
    },
    {
        translationKey: 'APPLICATION_SEARCH',
        toState: 'student.search',
        loginRequired: false,
        linkId: 'top-nav-student-course-unit-search',
    },
];

@Component({
    selector: 'app-student-top-navigation',
    template: '@if (items$ | async; as items) {<sis-top-navigation [mainNavigationItems]="items" [homeState]="homeState"/>}',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class StudentTopNavigationComponent {
    private readonly authService = inject(AuthService);
    private readonly universityService = inject(UniversityService);
    private readonly userSettingsService = inject(UserSettingsEntityService);

    readonly homeState = studentRoutes.defaultView;

    readonly items$: Observable<MainNavigationItem[]> = combineLatest([
        this.universityService.getCurrentUniversitySettings().pipe(catchError(() => of(null))),
        this.authService.userLoggedIn$,
        this.userSettingsService.getOwnSettings(),
    ]).pipe(
        map(([settings, loggedIn, userSettings]: [UniversitySettings | null, boolean, UserSettings | null]) =>
            ALL_ITEMS.filter(item => {
                switch (item.linkId) {
                    case 'top-nav-student-studies-structure':
                        return !settings?.frontendFeatureToggles?.forceNewPlanActive && !userSettings?.isNewPlanVersionInUse;
                    case NEW_PLAN_LINK_ID:
                        return (settings?.frontendFeatureToggles?.forceNewPlanActive || userSettings?.isNewPlanVersionInUse) ?? false;
                    default:
                        return true;
                }
            }).filter(item => loggedIn || !item.loginRequired),
        ),
    );
}
